import React, { useState, useEffect, useRef } from "react";
import * as Mui from "@mui/material";
import { themes } from "services/constants";
import { EditAndSidePeek } from "../PayrunSidePeak/EditAndSidePeek";
import { Employee } from "./Employee";
import { GroupDetails } from "./GroupDetails";
import Logs from "./Logs";
import { ImportedData } from "./ImportedData";
import { useFilter } from "pages/FilterContext";
import { tooltipStyleProps } from "services/constants/Styles";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { pdf } from "@react-pdf/renderer";
import { getTemplateById } from "pages/PayRoll/PayslipTemplates/templates";
import { Description, Downloading } from "@mui/icons-material";
import { useFetch } from "services/hooks/useFetch";
import { usePayrollCycleRun } from "../../PayrollCycleRunContext";
import { FilterProvider } from "pages/FilterContext";

const keys = [
  "user_name",
  "employee_id",
  "paid_days",
  "gross_pay",
  "total_deduction",
  "net_pay",
  "per_day_salary",
];

export const CostStructure = ({
  companyId,
  recallEmployee,
  setRecallEmployee,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, token, globalSearchValue, userScreenAccess, userDetails } =
    useSelector((state) => state.tokenReducer);
  const activeTemplateId = useSelector(
    (state) => state.userReducer.payslipTemplateId,
  );
  const BulkDocument = getTemplateById(activeTemplateId).BulkDocument;
  const { payScheduleId, payScheduleData, logData, setRecallLogsApi } =
    usePayrollCycleRun();
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const hasPendingIssues =
    logData?.length > 0 || payScheduleData?.status === "Draft";
  const baseTabs = [
    { key: "employee", label: "Employee" },
    { key: "group", label: "Group" },
    { key: "logs", label: "Logs" },
    { key: "imported", label: "Imported Data" },
  ];
  const tabs = hasPendingIssues
    ? [
        baseTabs.find((tab) => tab.key === "logs"),
        baseTabs.find((tab) => tab.key === "employee"),
        baseTabs.find((tab) => tab.key === "group"),
        baseTabs.find((tab) => tab.key === "imported"),
      ]
    : baseTabs;
  const [activeTab, setActiveTab] = useState(() =>
    hasPendingIssues ? "logs" : "employee",
  );
  const prevStatusRef = useRef(payScheduleData?.status);

  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = payScheduleData?.status;
    if (prevStatus === "Draft" && payScheduleData?.status !== "Draft") {
      if (logData?.length > 0) {
        setActiveTab("logs");
        setRecallLogsApi(true);
      } else {
        setActiveTab("employee");
      }
    }
  }, [payScheduleData?.status, logData, setRecallLogsApi]);
  const { setSelectedFilters, setFilterValues, setSortRules } = useFilter();
  const [isGeneratingBulkPayslip, setIsGeneratingBulkPayslip] = useState(false);
  const [costStructureDetailsState, setCostStructureDetailsState] = useState(
    [],
  );
  const [updateData, setUpdateData] = useState(true);
  const { result: payrunData } = useFetch(
    `${domain}payrun/retrieve/?pay_schedule_id=${payScheduleId}&company_id=${requestCompanyId}`,
  );

  const handleViewClosePanel = () => {
    setIsViewPanelOpen(false);
  };

  const payrollConfig = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Payroll Processing",
  );

  const subModuleId = payrollConfig?.submodules?.find(
    (sub) => sub.sub_module_name === "Payroll Cycle Run",
  )?.sub_module_id;

  const renderComponent = () => {
    switch (activeTab) {
      case "employee":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <Employee
              companyId={companyId}
              setIsViewPanelOpen={setIsViewPanelOpen}
              recallEmployee={recallEmployee}
              setRecallEmployee={setRecallEmployee}
              onNoEmployeeData={() => setActiveTab("logs")}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );
      case "group":
        return (
          <GroupDetails
            companyId={companyId}
            setIsViewPanelOpen={setIsViewPanelOpen}
          />
        );
      case "logs":
        return <Logs companyId={companyId} />;
      case "imported":
        return <ImportedData companyId={companyId} />;
      default:
        return <Employee companyId={companyId} />;
    }
  };

  useEffect(() => {
    if (
      updateData &&
      payrunData?.payroll_run_details?.length !== 0 &&
      payrunData
    ) {
      setCostStructureDetailsState(payrunData?.payroll_run_details);
      setUpdateData(false);
    }
  }, [payrunData, updateData]);

  useEffect(() => {
    if (payrunData?.length !== 0 || payrunData?.length === 0) {
      const filteredResults = payrunData?.payroll_run_details?.filter((item) =>
        keys.some((key) =>
          item[key]
            ?.toString()
            .toLowerCase()
            .includes(globalSearchValue.toString().toLowerCase()),
        ),
      );
      setCostStructureDetailsState(filteredResults);
    }
  }, [globalSearchValue, payrunData]);

  const generateBulkPayslip = async () => {
    if (!costStructureDetailsState?.length) {
      showToast("error", "No employee data available to generate payslips");
      return;
    }

    setIsGeneratingBulkPayslip(true);

    try {
      const employeesWithPayslipData = await Promise.all(
        costStructureDetailsState.map(async (employee) => {
          try {
            const response = await fetch(
              `${domain}payroll/operations/payrollprocessing/payrollruncycle/get-payslip-details/?user_id=${employee?.user_id}&pay_schedule_id=${payScheduleId}&company_id=${requestCompanyId}`,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `token ${token}`,
                },
              },
            );

            if (!response.ok) return null;

            const payslipData = await response.json();

            if (!payslipData || payslipData?.error) return null;

            return {
              ...employee,
              payslipData,
            };
          } catch (err) {
            console.error("Payslip fetch error:", employee?.user_name, err);
            return null;
          }
        }),
      );

      const validEmployees = employeesWithPayslipData.filter(
        (emp) => emp && emp?.payslipData,
      );

      if (!validEmployees.length) {
        showToast("error", "No valid payslip data available to generate PDF");
        return;
      }

      const blob = await pdf(
        <BulkDocument employeesPdfData={validEmployees} />,
      ).toBlob();

      if (!blob) {
        showToast("error", "Failed to generate PDF file");
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${payScheduleData?.name ?? "Payslips"}.pdf`;

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error(error);
      showToast("error", `Failed to generate PDF: ${error?.message}`);
    } finally {
      setIsGeneratingBulkPayslip(false);
    }
  };

  return (
    <>
      <Mui.Box sx={{ mb: 2, width: "100%" }}>
        <Mui.Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            gap: 2,
            flexDirection: {
              xs: "column",
              sm: "row",
            },
          }}
        >
          <Mui.Box
            sx={{
              display: "flex",
              backgroundColor: "grey.100",
              padding: 0.5,
              borderRadius: 1.5,
              gap: 0.5,
              width: {
                xs: "100%",
                sm: "fit-content",
              },
              justifyContent: {
                xs: "space-between",
                sm: "flex-start",
              },
            }}
          >
            {tabs.map((tab) => (
              <Mui.Box
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                  if (tab.key !== "logs") {
                    setSelectedFilters([]);
                    setFilterValues({});
                    setSortRules([]);
                  }
                }}
                sx={{
                  padding: "10px 24px",
                  backgroundColor:
                    activeTab === tab.key ? "white" : "transparent",
                  color: activeTab === tab.key ? "#09BD71" : "text.secondary",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                  borderRadius: 1,
                  minWidth: 100,
                  textAlign: "center",
                  flex: {
                    xs: 1,
                    sm: "unset",
                  },
                }}
              >
                {tab.label}
              </Mui.Box>
            ))}
          </Mui.Box>

          <Mui.Box
            sx={{
              flexGrow: 1,
              display: { xs: "none", sm: "block" },
            }}
          />

          {(activeTab === "employee" || activeTab === "group") &&
            payScheduleData?.status === "Approved" &&
            costStructureDetailsState?.length > 0 && (
              <Mui.Tooltip
                arrow
                slotProps={tooltipStyleProps}
                title="Download all employee payslip"
              >
                <Mui.Button
                  variant="contained"
                  size="medium"
                  onClick={generateBulkPayslip}
                  disabled={isGeneratingBulkPayslip}
                  startIcon={
                    isGeneratingBulkPayslip ? <Downloading /> : <Description />
                  }
                  sx={{
                    background: themes.primaryButton,
                    textTransform: "capitalize",
                    "&:hover": {
                      backgroundColor: themes.primaryButton,
                    },
                    color: themes.headLine,
                    mr: 1,
                  }}
                >
                  {isGeneratingBulkPayslip
                    ? "Downloading PDF..."
                    : "Generate Bulk Payslip"}
                </Mui.Button>
              </Mui.Tooltip>
            )}
        </Mui.Box>
      </Mui.Box>

      <Mui.Paper sx={{ width: "100%", height: "66vh" }}>
        {renderComponent()}
      </Mui.Paper>

      <Mui.Drawer
        anchor="right"
        open={isViewPanelOpen}
        onClose={(event, reason) => {
          handleViewClosePanel();
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: "680px",
            maxWidth: "90vw",
            backgroundColor: themes?.themes?.whiteColor,
          },
        }}
      >
        {isViewPanelOpen ? (
          <EditAndSidePeek
            onClose={handleViewClosePanel}
            recallEmployee={recallEmployee}
            setRecallEmployee={setRecallEmployee}
          />
        ) : null}
      </Mui.Drawer>
    </>
  );
};
